import React from 'react';
import { lightTheme as T } from '../../../Constants/Colors';
import { bodySmall, caption, fontWeight, Body, Heading } from '../../../Constants/Fonts';
import NoDataPage from '../../../Components/NoDataPage';

// PortfolioTrendChart — smooth line + area chart of open risks at each month-end.
// One point per month (height ∝ open risks) with the value above it and the
// monthly net change (new − closed) under each month. A KPI badge (top-right)
// shows the latest open count and the % change since the first month. The y-axis
// is framed around the data (not anchored at 0) so the trend reads clearly.
// Colours come from Colors.js; DOM text uses the shared Fonts.js components.
//
// Props: series — [{ month, open, net }]  (inflow/outflow accepted but unused)

const LINE = T.chartBlue;

// Catmull-Rom → cubic bezier: a smooth curve through the points.
function smoothLine(pts) {
    if (pts.length < 2) return pts.length ? `M ${pts[0].x} ${pts[0].y}` : '';
    let d = `M ${pts[0].x.toFixed(2)} ${pts[0].y.toFixed(2)}`;
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] || pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] || p2;
        const cp1x = p1.x + (p2.x - p0.x) / 6;
        const cp1y = p1.y + (p2.y - p0.y) / 6;
        const cp2x = p2.x - (p3.x - p1.x) / 6;
        const cp2y = p2.y - (p3.y - p1.y) / 6;
        d += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
    }
    return d;
}

// Round a raw step up to a "nice" 1/2/5×10ⁿ value for tidy gridlines.
const niceStep = (raw) => {
    const r = raw || 1;
    const pow = Math.pow(10, Math.floor(Math.log10(r)));
    const norm = r / pow;
    const nice = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
    return nice * pow;
};

// KPI badge — latest open count + % change since the first month. Exported so it
// can sit in the card header's action slot (on the same row as the title).
export function TrendBadge({ series = [] }) {
    if (!series.length) return null;
    const values = series.map((s) => s.open || 0);
    const first = values[0];
    const last = values[values.length - 1];
    const pct = first ? ((last - first) / first) * 100 : 0;
    const up = pct >= 0;
    const pctStr = `${up ? '+' : ''}${pct.toFixed(1)}%`;
    return (
        <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 16,
            background: T.surfaceMuted, border: `1px solid ${T.border}`,
            borderRadius: T.radiusLg, padding: '8px 16px',
        }}>
            <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
                <Heading as="span" variant={3} color="var(--color-text-primary)" style={{ margin: 0, lineHeight: 1.1 }}>{last}</Heading>
                <Body as="span" variant={2} color="var(--color-text-muted)" style={{ margin: 0 }}>Open risks</Body>
            </span>
            <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
                <Body as="span" weight="bold" color={up ? 'var(--color-success)' : 'var(--color-danger)'} style={{ margin: 0 }}>
                    {up ? '↑' : '↓'} {pctStr}
                </Body>
                <Body as="span" variant={2} color="var(--color-text-muted)" style={{ margin: 0 }}>since {series[0].month}</Body>
            </span>
        </div>
    );
}

export default function PortfolioTrendChart({ series = [], emptyHint = 'This chart populates once risks are created.' }) {
    if (!series.length) return <NoDataPage hint={emptyHint} />;

    const values = series.map((s) => s.open || 0);
    const dataMin = Math.min(...values);
    const dataMax = Math.max(...values);
    const span = Math.max(dataMax - dataMin, 1);
    const pad = Math.max(Math.round(span * 0.6), 5);
    const step = niceStep((span + 2 * pad) / 4);
    let yMin = Math.floor((dataMin - pad) / step) * step;
    const yMax = Math.ceil((dataMax + pad) / step) * step;
    if (yMin < 0) yMin = 0;
    const ticks = [];
    for (let v = yMin; v <= yMax + 0.001; v += step) ticks.push(Math.round(v));

    const margin = { top: 28, right: 24, bottom: 46, left: 42 };
    const innerW = 700;
    const innerH = 190;
    const W = innerW + margin.left + margin.right;
    const H = innerH + margin.top + margin.bottom;
    const n = series.length;

    const x = (i) => (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW);
    const y = (v) => innerH - ((v - yMin) / (yMax - yMin || 1)) * innerH;

    const pts = series.map((s, i) => ({ ...s, x: x(i), y: y(s.open || 0) }));
    const lineD = smoothLine(pts);
    const areaD = pts.length >= 2
        ? `${lineD} L ${pts[pts.length - 1].x.toFixed(2)} ${innerH} L ${pts[0].x.toFixed(2)} ${innerH} Z`
        : '';

    const axisText = { ...caption(), fill: 'var(--color-text-muted)' };
    const xLabel = { ...bodySmall(), fill: 'var(--color-text-secondary)' };
    const valLabel = { ...caption({ fontWeight: fontWeight.bold }), fill: 'var(--color-text-primary)' };
    const netLabel = (net) => ({ ...caption({ fontWeight: fontWeight.semibold }), fill: (net || 0) >= 0 ? T.success : T.danger });

    return (
        <div>
            <svg width="100%" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Open risks trend over the last 6 months" style={{ display: 'block' }}>
                <defs>
                    <linearGradient id="rp1TrendArea" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={LINE} stopOpacity="0.22" />
                        <stop offset="100%" stopColor={LINE} stopOpacity="0" />
                    </linearGradient>
                </defs>
                <g transform={`translate(${margin.left}, ${margin.top})`}>
                    {/* Y grid + labels */}
                    {ticks.map((t, i) => (
                        <g key={i}>
                            <line x1={0} y1={y(t)} x2={innerW} y2={y(t)} stroke="var(--color-border)" strokeDasharray="4 4" strokeWidth={1} />
                            <text x={-10} y={y(t) + 4} textAnchor="end" style={axisText}>{t}</text>
                        </g>
                    ))}

                    {/* Area fill + smooth line */}
                    {areaD && <path d={areaD} fill="url(#rp1TrendArea)" />}
                    <path d={lineD} fill="none" stroke={LINE} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />

                    {/* Points + value labels */}
                    {pts.map((p) => (
                        <g key={p.month}>
                            <circle cx={p.x} cy={p.y} r={5} fill={LINE} stroke={T.surface} strokeWidth={2} />
                            <text x={p.x} y={p.y - 12} textAnchor="middle" style={valLabel}>{p.open}</text>
                        </g>
                    ))}

                    {/* X labels + monthly net change */}
                    {pts.map((p) => (
                        <g key={p.month}>
                            <text x={p.x} y={innerH + 18} textAnchor="middle" style={xLabel}>{p.month}</text>
                            <text x={p.x} y={innerH + 34} textAnchor="middle" style={netLabel(p.net)}>
                                {(p.net || 0) >= 0 ? '+' : ''}{p.net || 0}
                            </text>
                        </g>
                    ))}
                </g>
            </svg>

            <div className="rp1_trend_legend" style={{ ...bodySmall({ color: 'var(--color-text-secondary)' }), paddingLeft: margin.left }}>
                <Body as="span"><i style={{ background: LINE }} />Open risks</Body>
                <Body as="span"><i style={{ background: T.success }} />Net = new − closed</Body>
            </div>
        </div>
    );
}
